import { MainHeader } from '@/components/page/MainHeader';
import { Loading } from '@/components/shared/Loading';
import { TenantSettingsBarProvider } from '@/pages/tenantSettings/TenantSettingsBarContext';
import { ConfirmationOnCloseManager } from '@/services/confirm/ConfirmationOnCloseManager';
import { EmployeeAvatarProvider } from '@/services/employee/EmployeeAvatarProvider';
import { TenantFeatureSettingsProvider } from '@/services/features/TenantFeatureSettingsProvider';
import { LeaveTypesProvider } from '@/services/leaveManagement/LeaveTypesProvider';
import { OptimiserProvider } from '@/services/optimiser/OptimiserService';
import { RosteringRealtimeProvider } from '@/services/realtime/RosteringRealtimeProvider';
import { LocationSettingsProvider } from '@/services/settings/LocationSettingsProvider';
import { StaffSettingsProvider } from '@/services/settings/StaffSettingsProvider';
import { SystemSettingsProvider } from '@/services/settings/SystemSettingsProvider';
import { FC, Suspense } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { LocationProvider } from '../../location/LocationService';
import { Permissions, useAuthenticationService } from '../authentication-service';
import { AuthenticationState, Role } from '../authentication.types';

interface AuthenticatedProps {
  permissions?: Permissions[];
  fullWidth?: boolean;
}

export interface pageType {
  name: string;
  to: string;
  // Controls whether the header renders this item as a simple link or a parent submenu.
  variant?: 'link' | 'submenu';
  children?: pageType[];
  locked?: boolean;
  rolesRequired?: string[];
  debug?: boolean;
  feature?: string;
  onlyDelegatedSuperAdmin?: boolean;
}

const shouldHideMainHeader = (pathname: string) => {
  // Preserve legacy full-screen flows that were built without the shared app header.
  return (
    pathname.startsWith('/clock-in-out') ||
    pathname === '/clock-in-cancel' ||
    pathname.startsWith('/clock-in-out-success') ||
    pathname === '/nfc' ||
    pathname === '/NC1yMzRsLWg0Y2st8Jkpg'
  );
};

const AuthenticatedLayout: FC<Pick<AuthenticatedProps, 'fullWidth'>> = ({ fullWidth }) => {
  const location = useLocation();

  return (
    <>
      <ConfirmationOnCloseManager />
      <div className="h-screen flex flex-col">
        {!shouldHideMainHeader(location.pathname) && <MainHeader fullWidth={fullWidth} />}
        <div className="flex-1 overflow-auto">
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </div>
      </div>
    </>
  );
};

export const Authenticated: FC<AuthenticatedProps> = ({ permissions, fullWidth }) => {
  const [authState, user, role] = useAuthenticationService(state => [state.state, state.user, state.role]);

  const location = useLocation();

  // If the user is not authenticated, redirect them to a logon page
  if (authState === AuthenticationState.Restoring) {
    return <Loading />;
  }

  if (authState === AuthenticationState.Unauthenticated) {
    return <Navigate to="/login" replace={true} state={{ restore: location.pathname }} />;
  }

  // If the user is not allowed here
  if (user && permissions && !permissions.every(i => user.profile.permission.includes(i))) {
    // This will need to be replaced with a real permission screen
    return null;
  }

  if (role === Role.SuperAdmin) {
    return (
      <TenantFeatureSettingsProvider>
        <TenantSettingsBarProvider>
          <EmployeeAvatarProvider>
            <AuthenticatedLayout fullWidth={fullWidth} />
          </EmployeeAvatarProvider>
        </TenantSettingsBarProvider>
      </TenantFeatureSettingsProvider>
    );
  }

  const tenantUserLayout = (
    <TenantFeatureSettingsProvider>
      <TenantSettingsBarProvider>
        <EmployeeAvatarProvider>
          <AuthenticatedLayout fullWidth={fullWidth} />
        </EmployeeAvatarProvider>
      </TenantSettingsBarProvider>
    </TenantFeatureSettingsProvider>
  );

  // Build the tenant-scoped provider tree explicitly by access level.
  // Staff should not mount management-only providers that fetch all employees.
  return (
    <LocationProvider>
      <SystemSettingsProvider>
        <LocationSettingsProvider>
          <RosteringRealtimeProvider>
            <OptimiserProvider>
              <LeaveTypesProvider>
                {role === Role.Admin || role === Role.Manager ? (
                  <StaffSettingsProvider>{tenantUserLayout}</StaffSettingsProvider>
                ) : (
                  tenantUserLayout
                )}
              </LeaveTypesProvider>
            </OptimiserProvider>
          </RosteringRealtimeProvider>
        </LocationSettingsProvider>
      </SystemSettingsProvider>
    </LocationProvider>
  );
};
